'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';

import { Button, Typography } from 'lighthouse';

import { AttributeCategoryNav } from '@/widgets/AttributeFilters/_components/AttributeCategoryNav';
import CatalogProductCard from '@/widgets/CatalogProductCard';
import { CatalogProductCardLoading } from '@/widgets/CatalogProductCard/CatalogProductCardLoading';
import { catalogItemFromOffer } from '@/widgets/CatalogProductCard/mappers';
import { InfiniteScrollList } from '@/widgets/InfiniteScrollList';
import ScrollFade from '@/widgets/ScrollFade';

import { GenerateFilePreviewUrl } from '@/utilities/GenerateFilePreviewUrl';
import { getProductDetailsHref } from '@/utilities/getProductDetailsHref';

import { useCityStore } from '@/stores/city/useCityStore';

import { TEHRAN_CITY_ID } from '@/constants/common';
import { DEFAULT_PRODUCT_OFFER_SORT_BY } from '@/constants/productSort';
import { checkoutCartPage } from '@/constants/routes';

import { StoreProductsModalHeader } from './_components/StoreProductsModalHeader';
import { storeModalDesktopIsland, storeModalMobileSheet } from './constants';
import { StoreProductsModalProps } from './models';

import { getFilteredProductsService, getServingCategoriesByStoreUuidService } from '@/client/services/vendor/methods';
import { OfferContent, ServingCategoryType } from '@/server/services/vendor/models';
import { Shop } from 'iconsax-react';

const PAGE_SIZE = 8;
const SCROLL_CONTAINER_ID = 'store-products-modal-scroll';
const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';
const SUCCESS_PHASE_DURATION_MS = 800;
const getExpandedHeight = (isDesktop: boolean, panel: HTMLDivElement | null, collapsedHeight: number) => {
    if (isDesktop) {
        const parentHeight = panel?.offsetParent instanceof HTMLElement ? panel.offsetParent.clientHeight : 0;
        return Math.max(parentHeight - 32, collapsedHeight);
    }

    return Math.min(window.innerHeight * 0.92, 56 * 16);
};

type ModalPhase = 'success' | 'expanded';

const StoreProductsModal = ({ open, storeUuid, storeName, excludedProductUuid, onClose }: StoreProductsModalProps) => {
    const cityId = useCityStore((state) => state.cityId);
    const [phase, setPhase] = useState<ModalPhase>('success');
    const [products, setProducts] = useState<OfferContent[]>([]);
    const [categories, setCategories] = useState<ServingCategoryType[]>([]);
    const [activeCategoryId, setActiveCategoryId] = useState<number | undefined>(undefined);
    const [isCategoriesReady, setIsCategoriesReady] = useState(false);
    const [isLastPage, setIsLastPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasProducts, setHasProducts] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);
    const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
    const [panelHeight, setPanelHeight] = useState<number | undefined>(undefined);
    const expandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activeCategoryIdRef = useRef<number | undefined>(undefined);
    const productsContextRef = useRef('');
    const productsContext = `${open}-${storeUuid}-${cityId}-${excludedProductUuid}`;
    useLayoutEffect(() => {
        productsContextRef.current = productsContext;
    }, [productsContext]);
    const headerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const fetchProducts = useCallback(
        async (page: number) => {
            if (!storeUuid) {
                return [];
            }

            const requestedCategoryId = activeCategoryIdRef.current;

            if (page === 0) {
                setIsLoading(true);
            }

            try {
                const response = await getFilteredProductsService({
                    storeUuid,
                    cityId: cityId || TEHRAN_CITY_ID,
                    page,
                    size: PAGE_SIZE,
                    sortBy: DEFAULT_PRODUCT_OFFER_SORT_BY,
                    ...(requestedCategoryId ? { categoryIds: [requestedCategoryId] } : {})
                });

                if (activeCategoryIdRef.current !== requestedCategoryId || productsContextRef.current !== productsContext) {
                    return [];
                }

                const content = response.data.content.filter(
                    (product) => product.discountPercentage !== 99 && product.productUuid !== excludedProductUuid
                );

                setIsLastPage(response.data.last);

                if (page === 0) {
                    setHasProducts(Boolean(content.length));
                    setProducts(content);
                }

                return content;
            } catch {
                if (page === 0 && activeCategoryIdRef.current === requestedCategoryId && productsContextRef.current === productsContext) {
                    setHasProducts(false);
                    setProducts([]);
                }

                return [];
            } finally {
                if (page === 0 && activeCategoryIdRef.current === requestedCategoryId && productsContextRef.current === productsContext) {
                    setIsLoading(false);
                }
            }
        },
        [cityId, excludedProductUuid, storeUuid, productsContext]
    );

    const clearExpandTimer = () => {
        if (expandTimerRef.current) {
            clearTimeout(expandTimerRef.current);
            expandTimerRef.current = null;
        }
    };

    useEffect(() => {
        setPortalTarget(document.getElementById('inner-app'));
    }, []);

    useEffect(() => {
        const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const updateViewport = () => setIsDesktop(mediaQuery.matches);

        updateViewport();
        mediaQuery.addEventListener('change', updateViewport);

        return () => mediaQuery.removeEventListener('change', updateViewport);
    }, []);

    useEffect(() => {
        if (!open || !storeUuid) {
            return;
        }

        setPhase('success');
        setProducts([]);
        setCategories([]);
        setActiveCategoryId(undefined);
        activeCategoryIdRef.current = undefined;
        setIsCategoriesReady(false);
        setIsLastPage(false);
        setHasProducts(false);

        const fetchCategories = async () => {
            try {
                const response = await getServingCategoriesByStoreUuidService(storeUuid);
                const nextCategories = response.data.content ?? [];
                const defaultCategoryId = nextCategories[0]?.id;

                setCategories(nextCategories);
                setActiveCategoryId(defaultCategoryId);
                activeCategoryIdRef.current = defaultCategoryId;
            } catch {
                setCategories([]);
                setActiveCategoryId(undefined);
                activeCategoryIdRef.current = undefined;
            } finally {
                setIsCategoriesReady(true);
            }
        };

        void fetchCategories();

        clearExpandTimer();
        expandTimerRef.current = setTimeout(() => {
            setPhase('expanded');
        }, SUCCESS_PHASE_DURATION_MS);

        return clearExpandTimer;
    }, [open, storeUuid]);

    useEffect(() => {
        if (!open || !storeUuid || !isCategoriesReady) {
            return;
        }

        setProducts([]);
        setIsLastPage(false);
        setHasProducts(false);
        void fetchProducts(0);
    }, [activeCategoryId, fetchProducts, isCategoriesReady, open, storeUuid]);

    useLayoutEffect(() => {
        if (!open) {
            setPanelHeight(undefined);
            return;
        }

        const collapsedHeight = headerRef.current?.offsetHeight;

        if (!collapsedHeight) {
            return;
        }

        if (phase === 'success') {
            setPanelHeight(collapsedHeight);
            return;
        }

        const expandedHeight = getExpandedHeight(isDesktop, panelRef.current, collapsedHeight);

        setPanelHeight(collapsedHeight);
        const frameId = requestAnimationFrame(() => {
            setPanelHeight(expandedHeight);
        });

        return () => cancelAnimationFrame(frameId);
    }, [categories.length, isDesktop, open, phase]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose, open]);

    const handleClose = () => {
        clearExpandTimer();
        onClose();
    };

    const handleCategorySelect = (categoryId: string) => {
        const nextCategoryId = Number(categoryId);
        activeCategoryIdRef.current = nextCategoryId;
        setActiveCategoryId(nextCategoryId);
    };

    const renderProduct = (product: OfferContent) => {
        return (
            <div key={product.productUuid} onClick={handleClose}>
                <CatalogProductCard
                    isCompact
                    forceMobile
                    item={catalogItemFromOffer(product, getProductDetailsHref(product.productUuid, product.productSlug, storeUuid))}
                    showSelectButton={false}
                />
            </div>
        );
    };

    if (!open || !portalTarget) {
        return null;
    }

    const isExpanded = phase === 'expanded';
    const categoryNav = {
        items: categories.map((category) => ({
            id: String(category.id),
            label: category.name,
            image: GenerateFilePreviewUrl({
                attachableId: String(category.id),
                attachment: 'CATEGORY',
                type: 'MAIN',
                isPrimary: true,
                sortOrder: 0,
                thumbnail: true
            }).src
        })),
        activeId: activeCategoryId == null ? null : String(activeCategoryId),
        hideBreadcrumb: true,
        onSelect: handleCategorySelect
    };

    const panel = (
        <div
            ref={panelRef}
            role='dialog'
            aria-modal
            aria-labelledby='store-products-modal-title'
            className={`z-10 flex flex-col bg-white transition-[height] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none
                ${isDesktop ? storeModalDesktopIsland : storeModalMobileSheet} ${
                    phase === 'success' ? 'animate-storeModalEnter motion-reduce:animate-none' : ''
                }`}
            style={{ height: panelHeight }}
            onClick={(event) => event.stopPropagation()}>
            <div ref={headerRef} className={`shrink-0 ${isExpanded ? 'border-b border-N100' : ''}`}>
                <div className='mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-N200 md:hidden' aria-hidden />
                <StoreProductsModalHeader onClose={handleClose} storeName={storeName} storeUuid={storeUuid} />
            </div>

            <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
                {isExpanded && (
                    <ScrollFade
                        className='flex min-h-0 flex-1 flex-col'
                        scrollerId={SCROLL_CONTAINER_ID}
                        scrollerClassName='nice-scroll min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-3'>
                        {categories.length > 0 && (
                            <div className='mb-3'>
                                <AttributeCategoryNav {...categoryNav} />
                            </div>
                        )}
                        <div className='flex flex-col gap-3'>
                            {isLoading && (
                                <div className='grid grid-cols-1 gap-3'>
                                    <CatalogProductCardLoading count={2} />
                                </div>
                            )}

                            {!isLoading && !hasProducts && (
                                <div
                                    className='flex flex-col items-center justify-center gap-2.5 rounded-lg border border-dashed border-N300 bg-white
                                        px-4 py-10 text-center'>
                                    <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-N50 text-blackLowEmphasis'>
                                        <Shop size={24} color='currentcolor' aria-hidden />
                                    </div>
                                    <Typography color='blackLowEmphasis' className='!m-0 text-sm'>
                                        {categories.length ? 'محصولی برای این دسته‌بندی موجود نیست.' : 'محصول دیگری از این فروشگاه یافت نشد.'}
                                    </Typography>
                                </div>
                            )}

                            {!isLoading && hasProducts && (
                                <InfiniteScrollList<OfferContent>
                                    key={`${storeUuid}-${excludedProductUuid ?? 'all'}-${activeCategoryId ?? 'all'}`}
                                    fetchData={fetchProducts}
                                    renderRow={renderProduct}
                                    containerProps={{ className: 'grid grid-cols-1 gap-3' }}
                                    endMessage={null}
                                    infiniteScrollProps={{
                                        scrollableTarget: SCROLL_CONTAINER_ID,
                                        loader: (
                                            <div className='mt-3 grid grid-cols-1 gap-3'>
                                                <CatalogProductCardLoading count={2} />
                                            </div>
                                        ),
                                        scrollThreshold: 0.85
                                    }}
                                    defaultItems={products}
                                    isLastPage={isLastPage}
                                />
                            )}
                        </div>
                    </ScrollFade>
                )}

                <div className='shrink-0 border-t border-N100 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:rounded-b-lg'>
                    <div className='flex flex-col items-stretch gap-2 md:flex-row md:items-center'>
                        <Link href={checkoutCartPage} onClick={handleClose} className='shrink-0'>
                            <Button type='button' variant='outlined' size='large' className='w-full whitespace-nowrap'>
                                مشاهده سبد خرید
                            </Button>
                        </Link>
                        <Link href={`/vendor/${storeUuid}`} onClick={handleClose} className='min-w-0 flex-1'>
                            <Button variant='contained' size='large' className='w-full whitespace-nowrap text-white'>
                                مشاهده محصولات این فروشگاه
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(
        <div className='fixed inset-0 z-[100]' role='presentation' onClick={handleClose}>
            <div className='absolute inset-0 animate-storeModalFadeIn bg-black/50 motion-reduce:animate-none' aria-hidden />

            {isDesktop ? (
                <div className='pointer-events-none fixed inset-0 z-10 flex justify-center'>
                    <div className='desktop-width pointer-events-none relative h-full w-full px-4 py-4'>{panel}</div>
                </div>
            ) : (
                panel
            )}
        </div>,
        portalTarget
    );
};

export default StoreProductsModal;
